<template>
  <div @click="close" class="modal-question-form" id="modal-question-form">
    <div class="modal-question-form-overlay">
      <div class="modal-question-form-content">

        <div
          @click="(e) => e.stopPropagation()"
          :class="[baseContainerClass, containerClass]">
          <button @click="close" class="modal-question-form-close">
            <img src="@/assets/img/X_close.svg" alt="close-icon" />
          </button>

          <div class="modal-question-form-banner-wrapper">
            <div :class="bannerClass">
              <h1 class="title title--gray" v-text="translations[lang].title" />
              <p class="subtitle" v-html="translations[lang].subtitle" />
            </div>
            <img v-if="showBannerImage" src="@/assets/img/introduction/plan.webp" alt="banner" />
          </div>

          <form id="modal-question-form" action="none" novalidate @submit.prevent="handleSubmit">
            <div :class="[baseInputsContainerClass, inputsContainerClass]">

              <label class="styled-input" :class="{ 'has-text': activeField === 'name', 'styled-input--error': errorMessage.name }">
                <div class="styled-input__container">
                  <img src="@/assets/img/question-form/name-icon.svg" alt="name-icon" />
                  <input
                    @input="clearError('name')"
                    @focus="activeField = 'name'"
                    @blur="activeField = null"
                    :placeholder="translations[lang].name_placeholder"
                    type="text"
                    name="name"
                    v-model="name"
                    data-question-form="name"/>
                </div>
                <p v-if="errorMessage.name" class="styled-input__error-text">{{ errorMessage.name[0] }}</p>
              </label>

              <label class="styled-input" :class="{ 'has-text': activeField === 'email', 'styled-input--error': errorMessage.email }">
                <div class="styled-input__container">
                  <img src="@/assets/img/question-form/email-icon.svg" alt="email-icon" />
                  <input
                    @input="clearError('email')"
                    @focus="activeField = 'email'"
                    @blur="activeField = null"
                    :placeholder="translations[lang].email_placeholder"
                    type="email"
                    name="email"
                    v-model="email"
                    data-question-form="email"/>
                </div>
                <p v-if="errorMessage.email" class="styled-input__error-text">{{ errorMessage.email[0] }}</p>
              </label>

              <label v-if="showCompanyUrl" class="styled-input" :class="{ 'has-text': activeField === 'companyUrl' }">
                <div class="styled-input__container">
                  <img src="@/assets/img/question-form/web-icon.svg" alt="web-icon" />
                  <input
                    @input="clearError('company_website')"
                    @focus="activeField = 'companyUrl'"
                    @blur="activeField = null"
                    :placeholder="translations[lang].companyUrl_placeholder"
                    type="url"
                    name="companyUrl"
                    v-model="companyUrl"
                    data-question-form="companyUrl"/>
                </div>
              </label>

              <label v-if="showPlansСount" class="styled-input" :class="{ 'has-text': activeField === 'plansСount' }">
                <div class="styled-input__container">
                  <img src="@/assets/img/question-form/units-icon.svg" alt="units-icon" />
                  <input
                    @input="clearError('plans_count')"
                    @focus="activeField = 'plansСount'"
                    @blur="activeField = null"
                    :placeholder="translations[lang].plansСount_placeholder"
                    name="plansСount"
                    v-model.number="plansСount"
                    data-question-form="plansСount"/>
                </div>
              </label>

              <label v-if="showQuestion" class="styled-input styled-input--full" :class="{ 'has-text': activeField === 'question' }">
                <slot name="custom-question-field">
                  <div class="styled-input__container">
                    <img src="@/assets/img/question-form/question-icon.svg" alt="question-icon" />
                    <input
                      @input="clearError('question')"
                      @focus="activeField = 'question'"
                      @blur="activeField = null"
                      :placeholder="translations[lang].question_placeholder"
                      type="text"
                      name="question"
                      v-model="question"
                      data-question-form="question"/>
                  </div>
                </slot>
              </label>
            </div>

            <label :class="[baseCheckboxContainerClass, checkboxContainerClass]">
              <input id="input-checkbox" type="checkbox" name="agree" v-model="privacyPolicyAccept" checked />
              <div id="styled-checkbox" class="styled-checkbox--box">
                <img src="@/assets/img/check-icon-corall.svg" alt="check icon" />
              </div>
              <p class="text-checkbox" v-html="translations[lang].privacy" />
              <p id="error-text-checkbox" class="error-text-checkbox">
                {{
                  !privacyPolicyAccept
                    ? (lang === 'ru'
                        ? 'Пожалуйста, подтвердите согласие на обработку данных'
                        : 'Please accept the privacy policy')
                    : ''
                }}
              </p>
            </label>

            <button
              :disabled="buttonDisabled"
              class="custom-button btn-send"
              id="submit"
              type="submit"
              v-text="translations[lang].send"/>

          </form>

          <img v-if="showHouseIcon" class="modal-question-form-house" src="@/assets/img/house-in-form-modal-icon.svg" alt="house-icon" />

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { api } from '@/api';
import { triggeringYandexMetric } from '@/assets/js/yandex-events';
import { triggeringGoogleTagMetric } from '@/assets/js/google-tag-metrics';

const baseContainerClass = 'modal-question-form-body';
const baseInputsContainerClass = 'modal-question-form__inputs';
const baseCheckboxContainerClass = 'styled-checkbox';

const props = defineProps({
  lang: {
    type: String,
    default: 'en',
  },
  close: {
    type: Function,
    required: true,
  },
  openMessage: {
    type: Function,
    required: true,
  },
  containerClass: {
    type: String,
    default: '',
  },
  bannerClass: {
    type: String,
    default: 'modal-question-form-banner',
  },
  inputsContainerClass: {
    type: String,
    default: '',
  },
  checkboxContainerClass: {
    type: String,
    default: '',
  },
  translations: {
    type: Object,
    required: true,
  },
  showBannerImage: {
    type: Boolean,
    default: false,
  },
  showCompanyUrl: {
    type: Boolean,
    default: false,
  },
  showPlansСount: {
    type: Boolean,
    default: false,
  },
  showQuestion: {
    type: Boolean,
    default: false,
  },
  showHouseIcon: {
    type: Boolean,
    default: false,
  },
  formType: {
    type: String,
    default: null,
  },
});

const name = ref('');
const email = ref('');
const companyUrl = ref('');
const plansСount = ref(null);
const question = ref('');
const privacyPolicyAccept = ref(true);
const buttonDisabled = ref(false);
const errorMessage = ref({});
const activeField = ref(null);

const formTypePrefixMap = {
  view: 'Вид',
  question: 'Вопрос',
  demo: 'Демо',
};

const messages = {
  en: {
    nameRequired: 'Please enter your name',
    emailRequired: 'Please enter your work email',
  },
  ru: {
    nameRequired: 'Пожалуйста, введите ваше имя',
    emailRequired: 'Пожалуйста, введите вашу рабочую почту',
  },
};

const submitQuestion = (params, apiMethod) => {
  buttonDisabled.value = true;

  apiMethod(params)
    .then(() => {
      if (process.env.NODE_ENV === 'production') {
        try {
          triggeringYandexMetric.submitFormQuestion();
          triggeringGoogleTagMetric.submitFormQuestion();
        } catch (error) {
          console.error(error);
        }
      }

      props.close();
      props.openMessage();
    })
    .catch((err) => {
      if (err.response && err.response.data.errors) {
        errorMessage.value = { ...err.response.data.errors };
      }
    })
    .finally(() => {
      buttonDisabled.value = false;
    });
};

const handleSubmit = (e) => {
  e.stopPropagation();
  e.preventDefault();

  if (!privacyPolicyAccept.value) return;

  const errors = {};
  const currentMessages = messages[props.lang];

  if (!name.value.trim()) {
    errors.name = [currentMessages.nameRequired];
  }
  if (!email.value.trim()) {
    errors.email = [currentMessages.emailRequired];
  }

  if (Object.keys(errors).length > 0) {
    errorMessage.value = errors;
    return;
  }

  if (props.formType === 'demo') {
    if (props.lang === 'en' && typeof window.ym === 'function') {
      window.ym(93847903, 'reachGoal', 'modal-question-form');
    } else if (props.lang === 'ru' && typeof window.ym === 'function') {
      window.ym(98352873, 'reachGoal', 'modal-question-form');
    }
  }

  const params = {
    name: name.value,
    email: email.value,
    ...(companyUrl.value && { company_website: companyUrl.value }),
    ...(plansСount.value && { plans_count: Number(plansСount.value) }),
    ...(question.value && {
      question: props.lang === 'ru' && props.formType
        ? `${formTypePrefixMap[props.formType]}: ${question.value}`
        : question.value,
    }),
  };

  submitQuestion(
    params,
    props.lang === 'ru' ? api.askQuestion : api.askQuestionEn,
  );
};

const clearError = (field) => {
  if (errorMessage.value[field]) {
    delete errorMessage.value[field];
  }
};
</script>
